import { Component, OnInit, OnDestroy, ViewContainerRef, ViewChild } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ResponsiveService } from 'src/app/shared/services/responsive.service';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { CustomViewService } from '../shared/services/custom-view.service';
import { SystemUserEulaService } from '../shared/services/system-user-eula.service';
import { CustomView } from '../shared/components/odata-filter/models/custom-view';
import { ViewContext } from '../shared/components/odata-filter/models/view-context';
import { PeopleFilterService } from '../people/people-filter.service';
import { TeamAddComponent } from '../teams/team-add/team-add.component';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { WidgetsService } from '../shared/services/widgets.service';
import { WidgetInstanceModel } from '../shared/models/widget.model';
import { WidgetUIType } from '../shared/models/widget.type';
import { LineupReleaseNotes } from './models/LineupReleaseNotes';
import { HomeService } from '../shared/services/home.service';
import { RecentUpdatesModalComponent } from '../shared/components/recent-updates-modal/recent-updates-modal.component';
import { AddWidgetDialogComponent } from './add-widget-dialog/add-widget-dialog.component';
import { WidgetDefinition, isWidgetModuleEnabled } from '../widgets/widget-registry';

@Component({
  selector: 'app-home',
  templateUrl: 'home.component.html',
  styleUrls: ['home.component.scss'],
  standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('sidenav', { static: true }) sidenav: MatSidenav;
  welcomeMessage = 'Welcome to Lineup!';
  teamDialogRef: MatDialogRef<TeamAddComponent>;
  private subscriptions = new Subscription();
  widgetsUpdateDateTime = new Date();
  readonly HOME_PAGE_WIDGETS_UI_TYPE = WidgetUIType;
  homePageWidgets: WidgetInstanceModel[] = [];
  editMode = false;
  releases: LineupReleaseNotes[] = [];
  recentUpdatesModalDialogRef: MatDialogRef<RecentUpdatesModalComponent>;
  allowTenant: boolean = false;

  constructor(
    public auth: AuthService,
    public appConfig: AppConfigService,
    private viewContainerRef: ViewContainerRef,
    private dialog: MatDialog,
    private router: Router,
    private responsiveService: ResponsiveService,
    private peopleFilterService: PeopleFilterService,
    private customViewService: CustomViewService,
    private widgetsService: WidgetsService,
    private systemUserEulaService: SystemUserEulaService,
    private homeService: HomeService
  ) {}

  peopleViews: CustomView[] = [];
  loggedInSystemUserHasLatestEula: boolean;
  pageLoaded: boolean = false;

  ngOnInit() {
    var tenantSubdomain = this.appConfig.tenant.subdomain?.toLocaleLowerCase();
    if (
      tenantSubdomain &&
      (tenantSubdomain == 'localhost' || tenantSubdomain == 'lineup' || tenantSubdomain == 'test')
    ) {
      this.allowTenant = true;
    }
    this.subscriptions.add(
      this.homeService.releases.subscribe((releaseNotes) => {
        this.releases = releaseNotes;
      })
    );
    this.subscriptions.add(
      this.customViewService.customViews.subscribe((result) => {
        if (result && result.length) {
          this.peopleViews = result.filter((x) => x.viewContext == ViewContext.People);
        }
      })
    );

    this.subscriptions.add(
      this.systemUserEulaService.hasLoggedInSystemUserAcceptedLatestEula.subscribe((data) => {
        if (data == null) return;
        this.loggedInSystemUserHasLatestEula = data;
        this.pageLoaded = true;
      })
    );

    if (this.appConfig.initialized) {
      this.subscriptions.add(
        this.widgetsService.getHomePageWidgets().subscribe((result) => {
          if (result) {
            this.homePageWidgets = result;
          }
        })
      );
      this.homeService.getReleases();
    }

    this.subscriptions.add(
      this.responsiveService.toggleSidenavEvent.subscribe(() => {
        this.sidenav.toggle();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  isStaff() {
    return this.auth.staffLevelAccess;
  }
  addEntry() {
    this.recentUpdatesModalDialogRef = this.dialog.open(RecentUpdatesModalComponent, {
      viewContainerRef: this.viewContainerRef
    });

    this.recentUpdatesModalDialogRef.afterClosed().subscribe((result) => {
      this.recentUpdatesModalDialogRef = null;
      if (result) {
        this.homeService.openSnackBar('New Release Entry Successfully Created', true);
      }
    });
  }
  editEntry(release: LineupReleaseNotes) {
    this.recentUpdatesModalDialogRef = this.dialog.open(RecentUpdatesModalComponent, {
      viewContainerRef: this.viewContainerRef,
      data: {
        release: release
      }
    });

    this.recentUpdatesModalDialogRef.afterClosed().subscribe((result) => {
      this.recentUpdatesModalDialogRef = null;
      if (result) {
        if (result == true) {
          this.homeService.openSnackBar('Release Entry Successfully Removed', true);
        } else {
          this.homeService.openSnackBar('Release Entry Successfully Updated', true);
        }
      }
    });
  }
  toggleEditMode() {
    this.editMode = !this.editMode;
  }

  get visibleWidgets(): WidgetInstanceModel[] {
    return this.homePageWidgets.filter((w) =>
      isWidgetModuleEnabled(w.widgetUIType, this.appConfig.tenant.modules)
    );
  }

  drop(event: CdkDragDrop<WidgetInstanceModel[]>) {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const visible = this.visibleWidgets;
    moveItemInArray(visible, event.previousIndex, event.currentIndex);
    this.saveWidgets(visible);
  }

  private saveWidgets(visible: WidgetInstanceModel[]) {
    let visibleIndex = 0;
    const widgets = this.homePageWidgets.map((w) =>
      isWidgetModuleEnabled(w.widgetUIType, this.appConfig.tenant.modules)
        ? visible[visibleIndex++]
        : w
    );
    this.homePageWidgets = widgets;
    this.widgetsService.saveHomePageWidgets(widgets);
  }

  removeWidget(widget: WidgetInstanceModel) {
    const widgets = this.homePageWidgets.filter((x) => x !== widget);
    if (widgets.length === 0) {
      this.homeService.openSnackBar('A homepage must have at least one widget.', false);
      return;
    }
    this.homePageWidgets = widgets;
    this.widgetsService.saveHomePageWidgets(widgets);
  }

  openAddWidgetDialog() {
    const dialogRef = this.dialog.open(AddWidgetDialogComponent, {
      viewContainerRef: this.viewContainerRef
    });

    dialogRef.afterClosed().subscribe((definition: WidgetDefinition) => {
      if (!definition) {
        return;
      }
      const widgets = this.homePageWidgets.concat(
        new WidgetInstanceModel({
          widgetUIType: definition.type,
          settings: definition.defaultSettings()
        })
      );
      this.homePageWidgets = widgets;
      this.widgetsService.saveHomePageWidgets(widgets);
    });
  }

  setFiltersAndGoToPeople(filterId: string) {
    let view = this.peopleViews.find((x) => x.id == filterId && x.isSystemView == true);
    if (view) {
      this.peopleViews.forEach((x) => (x.isSelected = false));
      this.peopleFilterService.selectView(view);
      this.router.navigate(['/people']);
    }
  }
  isAdmin() {
    return this.auth.adminLevelAccess;
  }

  openAddTeamDialog() {
    this.teamDialogRef = this.dialog.open(TeamAddComponent, {
      viewContainerRef: this.viewContainerRef
    });

    this.teamDialogRef.afterClosed().subscribe((teamId) => {
      if (teamId) {
        this.router.navigate(['/teams/' + teamId]);
      }
      this.teamDialogRef = null;
    });
  }
}
